import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useGetServicesQuery,
  useGetServiceCategoriesQuery,
  useGetHomepageSettingsQuery,
} from "../redux/services/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FloatingButtons from "../components/FloatingButtons";
import { createExcerpt } from "../utils/excerpt";
import { useTranslation } from "../hooks/useTranslation";
import { trackServicesPage } from "../utils/analytics";
import AOS from "aos";
import "aos/dist/aos.css";

const ServicesPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get("category");
  const [selectedCategory, setSelectedCategory] = useState(
    categoryParam || "all"
  );
  const { t, isArabic } = useTranslation();

  // Use RTK Query to fetch data
  const { data: servicesData, isLoading: loadingServices } =
    useGetServicesQuery();
  const { data: categoriesData, isLoading: loadingCategories } =
    useGetServiceCategoriesQuery();
  const { data: settingsResponse } = useGetHomepageSettingsQuery();

  const allServices =
    servicesData?.services?.filter((service) => service.is_active) || [];
  const categories =
    categoriesData?.categories?.filter((cat) => cat.is_active) || [];
  const logoUrl = settingsResponse?.settings?.branding?.logo || null;
  const loading = loadingServices || loadingCategories;

  // Initialize AOS and track page visit
  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: "ease-in-out",
      once: true,
      offset: 100,
    });
    trackServicesPage();
  }, []);

  // Update selected category when URL param changes
  useEffect(() => {
    const categoryParam = searchParams.get("category");
    if (categoryParam) {
      setSelectedCategory(categoryParam);
    } else {
      setSelectedCategory("all");
    }
  }, [searchParams]);

  const handleCategoryClick = (categoryId) => {
    setSelectedCategory(categoryId);
    // Update URL params
    if (categoryId === "all") {
      setSearchParams({});
    } else {
      setSearchParams({ category: categoryId });
    }
  };

  const handleServiceClick = (serviceId) => {
    navigate(`/service/${serviceId}`);
  };

  const filteredServices =
    selectedCategory === "all"
      ? allServices
      : allServices.filter(
          (service) => service.category_id == selectedCategory
        );

  if (loading) {
    return (
      <div className="services-page">
        <div className="container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>{t("services.loadingServices")}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="services-page">
      <Header />
      <div className="container">
        {/* Page Title */}
        <div
          className="page-title-section"
          data-aos="fade-up"
          data-aos-delay="100"
        >
          <h1>{t("services.title")}</h1>
          <p>{t("services.description")}</p>
        </div>

        {/* Category Tabs */}
        <div className="category-tabs" data-aos="fade-up" data-aos-delay="200">
          <button
            className={`tab ${selectedCategory === "all" ? "active" : ""}`}
            onClick={() => handleCategoryClick("all")}
          >
            {t("services.allCategories")}
          </button>
          {categories.map((category) => (
            <button
              key={category.id}
              className={`tab ${
                selectedCategory == category.id ? "active" : ""
              }`}
              onClick={() => handleCategoryClick(category.id)}
            >
              {isArabic && category.title_ar
                ? category.title_ar
                : category.title}
            </button>
          ))}
        </div>

        {/* Services Grid */}
        <div className="services-grid-page">
          {filteredServices.length === 0 ? (
            <div
              className="no-services"
              data-aos="fade-up"
              data-aos-delay="300"
            >
              <p>{t("services.noServicesInCategory")}</p>
            </div>
          ) : (
            filteredServices.map((service, index) => (
              <div
                key={service.id}
                className="service-item clickable-service-item"
                data-aos="fade-up"
                data-aos-delay={`${300 + index * 100}`}
                onClick={() => handleServiceClick(service.id)}
                style={{ cursor: "pointer" }}
              >
                <div className="service-content">
                  <div className="service-title-box">
                    <span>
                      {isArabic && service.title_ar
                        ? service.title_ar
                        : service.title}
                    </span>
                  </div>
                  <div className="service-description">
                    {createExcerpt(
                      isArabic && service.content_ar
                        ? service.content_ar
                        : service.content,
                      20
                    )}
                  </div>
                </div>
                <div className="service-image-container">
                  {service.image_url ? (
                    <img
                      src={service.image_url}
                      alt={
                        isArabic && service.title_ar
                          ? service.title_ar
                          : service.title
                      }
                      className="service-image"
                    />
                  ) : (
                    <div className="service-placeholder">
                      <span>{t("services.servicePic")}</span>
                    </div>
                  )}
                </div>
                {index < filteredServices.length - 1 && (
                  <div className="service-separator"></div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
      {/* Footer */}
      <Footer />
      {/* Floating Buttons */}
      <FloatingButtons />
    </div>
  );
};

export default ServicesPage;
